import DoneIcon from "@mui/icons-material/Done";
import {
    Box,
    Card,
    CardActionArea,
    Chip,
    Grid,
    Typography,
} from "@mui/material";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
    MAX_ADDON_DEPTH,
    categorizeAddonSelection,
    createExtraAddonSelection,
    createOptionSelection,
    getApplicableBundleCategories,
    getAddonCode,
    getAllowedAddonCodes,
    getLookupItem,
    hasItemConfiguration,
    isItemCustomizationEnabled,
    normalizeBundleDefinition,
} from "./addonTree";

const selectionLabel = (option, itemLookup) => {
    const code = getAddonCode(option);
    const meta = getLookupItem(itemLookup, code);
    return option?.label || meta?.item_name || code;
};

export default function NestedItemConfigurator({
    item,
    value,
    onChange,
    itemLookup,
    isTabsPos,
    showValidation,
    visited = new Set(),
    depth = 0,
}) {
    const { t } = useTranslation();
    const addons = useMemo(() => (Array.isArray(value) ? value : []), [value]);
    const bundle = useMemo(
        () => normalizeBundleDefinition(item?.menu_item_bundle),
        [item?.menu_item_bundle]
    );
    const nestedBundle = useMemo(
        () => ({
            ...(bundle || {}),
            categories: getApplicableBundleCategories(bundle, { nested: true }),
        }),
        [bundle]
    );
    const allowedAddonCodes = useMemo(() => getAllowedAddonCodes(item), [item]);
    const { byCategory, usedIndices } = useMemo(
        () => categorizeAddonSelection(nestedBundle, addons),
        [nestedBundle, addons]
    );
    const itemCode = item?.item_code || item?.name;
    const nextVisited = useMemo(() => {
        const next = new Set(visited);
        if (itemCode) next.add(itemCode);
        return next;
    }, [visited, itemCode]);

    const replaceCategorySelection = (category, option) => {
        const selected = byCategory[category?.name] || [];
        const isSelected = selected.some(
            (node) => getAddonCode(node) === getAddonCode(option)
        );
        let next = addons.filter((node) => !selected.includes(node));

        if (category?.allow_multiple_select) {
            next = [...next, ...selected.filter((node) => getAddonCode(node) !== getAddonCode(option))];
            if (!isSelected) {
                next.push(
                    createOptionSelection(option, category?.name, itemLookup, {
                        visited: nextVisited,
                        depth,
                    })
                );
            }
        } else if (!isSelected) {
            next.push(
                createOptionSelection(option, category?.name, itemLookup, {
                    visited: nextVisited,
                    depth,
                })
            );
        } else {
            next.push(...selected);
        }

        onChange?.(next);
    };

    const updateNestedAddons = (selectedNode, nestedAddons) => {
        onChange?.(
            addons.map((node) =>
                node === selectedNode ? { ...node, addons: nestedAddons } : node
            )
        );
    };

    const extraNodes = addons.filter(
        (node, index) =>
            !usedIndices.has(index) && allowedAddonCodes.includes(getAddonCode(node))
    );

    const toggleExtraAddon = (code) => {
        const existing = extraNodes.find((node) => getAddonCode(node) === code);
        onChange?.(
            existing
                ? addons.filter((node) => node !== existing)
                : [...addons, createExtraAddonSelection(code, itemLookup)]
        );
    };

    if (depth >= MAX_ADDON_DEPTH || nextVisited.size > MAX_ADDON_DEPTH) {
        return (
            <Typography variant="caption" color="text.secondary">
                {t("bundle.maximumCustomizationDepth")}
            </Typography>
        );
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                borderLeft: depth > 0 ? "3px solid #e5e9f2" : "none",
                pl: depth > 0 ? 1.5 : 0,
            }}
        >
            {(nestedBundle?.categories || []).map((category) => {
                const selected = byCategory[category?.name] || [];
                const missing = !!showValidation && !!category?.mandatory && selected.length === 0;

                return (
                    <Box key={`${itemCode}_${category?.name}`}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: missing ? "#d32f2f" : "#6E7187",
                                fontWeight: 900,
                                textTransform: "uppercase",
                                display: "block",
                                mb: 0.75,
                            }}
                        >
                            {category?.name || t("bundle.options")}
                            {missing ? ` — ${t("bundle.required")}` : ""}
                        </Typography>

                        <Grid container spacing={1}>
                            {(category?.options || []).map((option) => {
                                const code = getAddonCode(option);
                                const selectedNode = selected.find(
                                    (node) => getAddonCode(node) === code
                                );
                                const isSelected = !!selectedNode;
                                const childItem = getLookupItem(itemLookup, code);
                                const canCustomize =
                                    isItemCustomizationEnabled(option) &&
                                    hasItemConfiguration(childItem) &&
                                    !nextVisited.has(code);

                                return (
                                    <Grid item xs={6} sm={4} md={3} key={`${category?.name}_${code}`}>
                                        <Card
                                            variant="outlined"
                                            sx={{
                                                borderColor: isSelected ? "#1976d2" : "#e5e9f2",
                                                borderWidth: isSelected && isTabsPos ? 2 : 1,
                                                bgcolor: isSelected
                                                    ? "rgba(25, 118, 210, 0.08)"
                                                    : "#fff",
                                            }}
                                        >
                                            <CardActionArea
                                                onClick={() => replaceCategorySelection(category, option)}
                                                sx={{ p: 1, minHeight: 62 }}
                                            >
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 800,
                                                                whiteSpace: "nowrap",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                            }}
                                                        >
                                                            {selectionLabel(option, itemLookup)}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            +{Number(option?.added_price) || 0}
                                                        </Typography>
                                                    </Box>
                                                    {isSelected ? <DoneIcon color="primary" fontSize="small" /> : null}
                                                </Box>
                                                {canCustomize ? (
                                                    <Chip
                                                        size="small"
                                                        label={t("bundle.customizable")}
                                                        sx={{ mt: 0.75, height: 20, fontSize: 10 }}
                                                    />
                                                ) : null}
                                            </CardActionArea>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>

                        {selected.map((selectedNode) => {
                            const option = (category?.options || []).find(
                                (candidate) => getAddonCode(candidate) === getAddonCode(selectedNode)
                            );
                            const childItem = getLookupItem(itemLookup, getAddonCode(selectedNode));
                            if (
                                !option ||
                                !isItemCustomizationEnabled(option) ||
                                !hasItemConfiguration(childItem) ||
                                nextVisited.has(getAddonCode(selectedNode))
                            ) {
                                return null;
                            }

                            return (
                                <Box
                                    key={`nested_${category?.name}_${getAddonCode(selectedNode)}`}
                                    sx={{ mt: 1.25, p: 1.25, bgcolor: "#f8f9fc", borderRadius: 1 }}
                                >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                                        {t("bundle.customizeItem", {
                                            item: selectionLabel(selectedNode, itemLookup),
                                        })}
                                    </Typography>
                                    <NestedItemConfigurator
                                        item={childItem}
                                        value={selectedNode?.addons || []}
                                        onChange={(nested) => updateNestedAddons(selectedNode, nested)}
                                        itemLookup={itemLookup}
                                        isTabsPos={isTabsPos}
                                        showValidation={showValidation}
                                        visited={nextVisited}
                                        depth={depth + 1}
                                    />
                                </Box>
                            );
                        })}
                    </Box>
                );
            })}

            {allowedAddonCodes.length > 0 ? (
                <Box>
                    <Typography
                        variant="caption"
                        sx={{
                            color: "#6E7187",
                            fontWeight: 900,
                            textTransform: "uppercase",
                            display: "block",
                            mb: 0.75,
                        }}
                    >
                        {t("bundle.extraAddons")}
                    </Typography>
                    <Grid container spacing={1}>
                        {allowedAddonCodes.map((code) => {
                            const isSelected = extraNodes.some(
                                (node) => getAddonCode(node) === code
                            );
                            const addonItem = getLookupItem(itemLookup, code);
                            return (
                                <Grid item xs={6} sm={4} md={3} key={`extra_${itemCode}_${code}`}>
                                    <Card
                                        variant="outlined"
                                        sx={{
                                            borderColor: isSelected ? "#2e7d32" : "#e5e9f2",
                                            bgcolor: isSelected
                                                ? "rgba(46, 125, 50, 0.08)"
                                                : "#fff",
                                        }}
                                    >
                                        <CardActionArea onClick={() => toggleExtraAddon(code)} sx={{ p: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                                {addonItem?.item_name || code}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                +{Number(addonItem?.standard_rate) || 0}
                                            </Typography>
                                        </CardActionArea>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Box>
            ) : null}
        </Box>
    );
}
